/**
 * Partner Service - Refactored
 * Now delegates to specialized services
 * Maintains backward compatibility with existing code
 */

import { Partner, Shop, MembershipPlan, ProductReview, ShopReview, PartnerType } from '../types';
import { partnerRegistrationService } from './partnerRegistrationService';
import { shopService } from './shopService';
import { shopFollowService } from './shopFollowService';
import { shopCustomizationService } from './shopCustomizationService';
import { reviewService } from './reviewService';
import { reviewModerationService } from './reviewModerationService';
import { partnerRepository } from '../../data/partnerRepository';
import { DocumentSnapshot, QueryDocumentSnapshot } from 'firebase/firestore';

// Types for backward compatibility
export interface CreatePartnerRequest {
    firebaseUid: string;
    email: string;
    name: string;
    phone: string;
    dni: string;
    type: PartnerType;
    businessName?: string;
    membership: MembershipPlan;
}

export interface CreateShopRequest {
    partnerId: string;
    name: string;
    slug: string;
    description: string;
    categories: string[];
    contactInfo: {
        phone: string;
        email: string;
        whatsapp?: string;
        address?: string;
    };
}

export interface RegistrationResult {
    success: boolean;
    partnerId?: string;
    shopId?: string;
    error?: string;
}

export interface ShopUpdateRequest {
    shopId: string;
    updates: Partial<Shop>;
}

export const partnerService = {
    // ==================== REGISTRATION (delegated to partnerRegistrationService) ====================
    registerPartnerAndShop: partnerRegistrationService.registerPartnerAndShop.bind(partnerRegistrationService),
    checkSlugAvailability: partnerRegistrationService.checkSlugAvailability.bind(partnerRegistrationService),
    isValidSlug: (slug: string): boolean => {
        // This function was internal, implementing inline
        const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
        return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 50;
    },
    calculateExpiryDate: (startDate: string): string => {
        // This function was internal, implementing inline
        const date = new Date(startDate);
        date.setMonth(date.getMonth() + 1);
        return date.toISOString();
    },
    getMembershipLimits: partnerRegistrationService.getMembershipLimits.bind(partnerRegistrationService),

    // ==================== PARTNERS ====================
    async getPartnerByUserId(firebaseUid: string): Promise<Partner | undefined> {
        return await partnerRepository.getPartnerByFirebaseUid(firebaseUid) as Partner | undefined;
    },

    async getPartnerById(partnerId: string): Promise<Partner | undefined> {
        return await partnerRepository.findById(partnerId) as Partner | undefined;
    },

    async getPartnersByIds(partnerIds: string[]): Promise<Partner[]> {
        return await partnerRepository.getPartnersByIds(partnerIds) as Partner[];
    },

    async updatePartner(partnerId: string, updates: Partial<Partner>): Promise<void> {
        await partnerRepository.updatePartner(partnerId, updates as any);
    },

    async getAllPartners(lastDoc?: DocumentSnapshot | null, pageSize: number = 100): Promise<{ partners: Partner[], lastDoc: DocumentSnapshot | null }> {
        const result = await partnerRepository.getAllPartners(
            lastDoc as QueryDocumentSnapshot<Partner> | null, 
            pageSize
        );
        return {
            partners: result.partners as Partner[],
            lastDoc: result.lastDoc as DocumentSnapshot | null
        };
    },

    // ==================== SHOPS (delegated to shopService) ====================
    getShopById: shopService.getShopById.bind(shopService),
    getShopBySlug: shopService.getShopBySlug.bind(shopService),
    getActiveShops: shopService.getActiveShops.bind(shopService),
    getAllShops: (pageSize?: number, filters?: { featured?: boolean; verified?: boolean }, lastDoc?: DocumentSnapshot | null) => shopService.getAllShops(pageSize, filters, lastDoc as any),
    getPendingShops: shopService.getPendingShops.bind(shopService),
    updateShop: shopService.updateShop.bind(shopService),
    createShop: shopService.createShop.bind(shopService),
    getFeaturedShops: shopService.getFeaturedShops.bind(shopService),
    getShopsByCategory: shopService.getShopsByCategory.bind(shopService),
    searchShops: shopService.searchShops.bind(shopService),
    getShopsByIds: shopService.getShopsByIds.bind(shopService),
    approveShop: shopService.approveShop.bind(shopService),
    rejectShop: shopService.rejectShop.bind(shopService),

    // ==================== FOLLOW (delegated to shopFollowService) ====================
    followShop: shopFollowService.followShop.bind(shopFollowService),
    unfollowShop: shopFollowService.unfollowShop.bind(shopFollowService),
    isFollowingShop: shopFollowService.isFollowingShop.bind(shopFollowService),
    getShopsFollowingByUser: shopFollowService.getShopsFollowingByUser.bind(shopFollowService),
    getFollowingShopIds: shopFollowService.getFollowingShopIds.bind(shopFollowService),

    // ==================== CUSTOMIZATION (delegated to shopCustomizationService) ====================
    getShopBanners: shopCustomizationService.getShopBanners.bind(shopCustomizationService),
    addShopBanner: shopCustomizationService.addShopBanner.bind(shopCustomizationService),
    updateShopBanner: shopCustomizationService.updateShopBanner.bind(shopCustomizationService),
    deleteShopBanner: shopCustomizationService.deleteShopBanner.bind(shopCustomizationService),
    updateShopTheme: shopCustomizationService.updateShopTheme.bind(shopCustomizationService),
    updateShopLayout: shopCustomizationService.updateShopLayout.bind(shopCustomizationService),

    // ==================== REVIEWS (delegated to reviewService and reviewModerationService) ====================
    getProductReviews: reviewService.getProductReviews.bind(reviewService),
    getShopReviews: reviewService.getShopReviews.bind(reviewService),
    createProductReview: reviewService.createProductReview.bind(reviewService),
    createShopReview: reviewService.createShopReview.bind(reviewService),
    respondToProductReview: reviewService.respondToProductReview.bind(reviewService),
    respondToShopReview: reviewService.respondToShopReview.bind(reviewService),
    getProductReviewsByPartner: reviewService.getProductReviewsByPartner.bind(reviewService),
    getShopReviewsByPartner: reviewService.getShopReviewsByPartner.bind(reviewService),

  // Admin moderation (delegated to reviewModerationService)
  getPendingProductReviews: reviewModerationService.getPendingProductReviews.bind(reviewModerationService),
  getPendingShopReviews: reviewModerationService.getPendingShopReviews.bind(reviewModerationService),
  getProductReviewsByStatus: reviewModerationService.getProductReviewsByStatus.bind(reviewModerationService),
  getShopReviewsByStatus: reviewModerationService.getShopReviewsByStatus.bind(reviewModerationService),
  approveProductReview: reviewModerationService.approveProductReview.bind(reviewModerationService),
  approveShopReview: reviewModerationService.approveShopReview.bind(reviewModerationService),
  rejectProductReview: reviewModerationService.rejectProductReview.bind(reviewModerationService),
  rejectShopReview: reviewModerationService.rejectShopReview.bind(reviewModerationService)
};
